/**
 * Orchestrazione. Schermate: MENU (scelta pista) -> PLAY (ready/countdown/running/
 * finished). Loop a TIMESTEP FISSO con accumulatore; il render interpola tra i due
 * ultimi stati della sim. L'input è campionato una volta per step e registrato
 * (InputRecorder) → replay identico = ghost + verify server (Sprint 3).
 *
 * Novità di questo giro:
 *  - Home con catalogo piste (pista di oggi + preset per difficoltà + random), PB per pista.
 *  - Onboarding contestuale (TIENI/LASCIA) alla primissima run.
 *  - Feedback "pompata efficace" (boost) passato al renderer: scia accesa + scintille.
 */

import { makeTerrain, type Terrain } from '../game/terrain';
import { initSim, step, cloneState, type SimState } from '../game/sim';
import { InputRecorder, sampleGhost, type GhostFrame, type Replay } from '../game/replay';
import * as C from '../game/constants';
import { clamp, lerp } from '../game/dmath';
import { PointerInput } from './input';
import { t } from '../lib/i18n';
import { tracking } from '../lib/tracker';
import {
  PRESETS,
  dailyTrack,
  randomTrack,
  difficultyDots,
  type TrackDef,
} from '../lib/tracks';

type Screen = 'menu' | 'play';
type Phase = 'ready' | 'countdown' | 'running' | 'finished';

const COUNTDOWN_MS = 2600;
const TUTORIAL_KEY = 'wake:tutorial-done';
const pbKey = (seed: string) => `wake:pb:${seed}`;
const fmt = (ms: number) => (ms / 1000).toFixed(2);

export class App {
  private readonly input: PointerInput;
  private readonly renderer: import('./render').WakeRenderer;

  private screen: Screen = 'menu';
  private phase: Phase = 'ready';

  private track: TrackDef = dailyTrack();
  private terrain: Terrain;
  private sim: SimState;
  private prev: SimState;
  private recorder = new InputRecorder();
  private acc = 0;
  private lastFrame = 0;
  private countdownStart = 0;
  private menuPan = 0;

  private pb: Replay | null = null;
  private ghost: GhostFrame[] | null = null;
  private tutorialDone = false;

  private readonly els: {
    menu: HTMLElement;
    trackList: HTMLElement;
    hud: HTMLElement;
    timer: HTMLElement;
    best: HTMLElement;
    start: HTMLElement;
    startName: HTMLElement;
    finish: HTMLElement;
    finishTime: HTMLElement;
    finishNote: HTMLElement;
    retry: HTMLButtonElement;
    change: HTMLButtonElement;
  };

  constructor(root: HTMLElement, RendererCtor: typeof import('./render').WakeRenderer) {
    root.innerHTML = `
      <canvas id="stage"></canvas>
      <div class="overlay menu" id="menu">
        <div class="brand small">WAKE</div>
        <div class="tagline">${t('tagline')}</div>
        <div class="menu-h">${t('menu.tracks')}</div>
        <div class="tracklist" id="tracklist"></div>
      </div>
      <div class="hud hidden" id="hud">
        <div class="timer" id="timer">0.00</div>
        <div class="best" id="best"></div>
      </div>
      <div class="overlay start hidden" id="start">
        <div class="start-name" id="startName"></div>
        <div class="hint">${t('hint.hold')}</div>
        <div class="cta">${t('hint.start')}</div>
      </div>
      <div class="overlay finish hidden" id="finish">
        <div class="ftitle">${t('finish.title')}</div>
        <div class="ftime" id="ftime">0.00</div>
        <div class="fnote" id="fnote"></div>
        <button class="retry" id="retry">${t('action.retry')}</button>
        <button class="change" id="change">${t('action.change')}</button>
      </div>`;

    const canvas = root.querySelector<HTMLCanvasElement>('#stage')!;
    this.els = {
      menu: root.querySelector('#menu')!,
      trackList: root.querySelector('#tracklist')!,
      hud: root.querySelector('#hud')!,
      timer: root.querySelector('#timer')!,
      best: root.querySelector('#best')!,
      start: root.querySelector('#start')!,
      startName: root.querySelector('#startName')!,
      finish: root.querySelector('#finish')!,
      finishTime: root.querySelector('#ftime')!,
      finishNote: root.querySelector('#fnote')!,
      retry: root.querySelector('#retry')!,
      change: root.querySelector('#change')!,
    };

    try {
      this.tutorialDone = localStorage.getItem(TUTORIAL_KEY) === '1';
    } catch {
      /* no storage */
    }

    this.terrain = makeTerrain(this.track.seed, this.track.difficulty);
    this.renderer = new RendererCtor(canvas, this.terrain);
    this.input = new PointerInput(root);
    this.sim = initSim(this.terrain);
    this.prev = cloneState(this.sim);

    this.els.retry.addEventListener('click', (e) => {
      e.stopPropagation();
      this.enterReady();
    });
    this.els.change.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openMenu();
    });
    window.addEventListener('resize', () => this.renderer.resize());

    // Tracking Lupo Game (vibecanyon.com/admin): sessione avviata + chiusura pagina.
    tracking.gameStart();
    window.addEventListener('pagehide', () =>
      tracking.gameEnd({ duration: Math.round(performance.now() / 1000) }),
    );

    this.openMenu();
    requestAnimationFrame((ts) => this.frame(ts));
  }

  // --- Menu ---

  private openMenu(): void {
    this.screen = 'menu';
    this.els.menu.classList.remove('hidden');
    this.els.hud.classList.add('hidden');
    this.els.start.classList.add('hidden');
    this.els.finish.classList.add('hidden');
    this.buildMenu();
  }

  private buildMenu(): void {
    const list = this.els.trackList;
    list.innerHTML = '';
    const defs: TrackDef[] = [dailyTrack(), ...PRESETS, randomTrack()];
    for (const def of defs) {
      const best = this.readPB(def.seed);
      const card = document.createElement('button');
      card.className = 'track-card' + (def.kind === 'daily' ? ' daily' : '');
      card.innerHTML = `
        <span class="tc-name">${t(def.nameKey)}</span>
        <span class="tc-diff">${difficultyDots(def.difficulty)}</span>
        <span class="tc-pb">${best ? `${t('menu.pb')} ${fmt(best.timeMs)}` : '—'}</span>`;
      card.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectTrack(def);
      });
      list.appendChild(card);
    }
  }

  private selectTrack(def: TrackDef): void {
    this.track = def;
    this.terrain = makeTerrain(def.seed, def.difficulty);
    this.renderer.setTerrain(this.terrain);
    this.loadPB(def.seed);
    this.screen = 'play';
    this.els.menu.classList.add('hidden');
    this.enterReady();
  }

  // --- Persistenza PB / ghost (per pista) ---

  private readPB(seed: string): Replay | null {
    try {
      const raw = localStorage.getItem(pbKey(seed));
      if (!raw) return null;
      const pb = JSON.parse(raw) as Replay;
      return pb && pb.seed === seed && Array.isArray(pb.toggles) ? pb : null;
    } catch {
      return null;
    }
  }

  private loadPB(seed: string): void {
    this.pb = this.readPB(seed);
    this.ghost = this.pb ? sampleGhost(this.terrain, this.pb) : null;
    this.updateBestLabel();
  }

  private savePB(pb: Replay): void {
    this.pb = pb;
    this.ghost = sampleGhost(this.terrain, pb);
    try {
      localStorage.setItem(pbKey(pb.seed), JSON.stringify(pb));
    } catch {
      /* niente storage */
    }
    this.updateBestLabel();
  }

  private updateBestLabel(): void {
    this.els.best.textContent = this.pb ? `${t('hud.best')} ${fmt(this.pb.timeMs)}` : '';
  }

  // --- Ciclo di vita della run ---

  private enterReady(): void {
    this.sim = initSim(this.terrain);
    this.prev = cloneState(this.sim);
    this.recorder = new InputRecorder();
    this.renderer.reset();
    this.acc = 0;
    this.phase = 'ready';
    this.els.finish.classList.add('hidden');
    this.els.hud.classList.remove('hidden');
    this.els.startName.textContent = t(this.track.nameKey);
    this.els.start.classList.remove('hidden');
    this.updateBestLabel();
  }

  private startCountdown(): void {
    this.phase = 'countdown';
    this.countdownStart = this.lastFrame;
    this.els.start.classList.add('hidden');
  }

  private startRun(): void {
    this.phase = 'running';
    this.sim = initSim(this.terrain);
    this.prev = cloneState(this.sim);
    this.recorder = new InputRecorder();
    this.acc = 0;
    tracking.runStart({ track: this.track.id, difficulty: this.track.difficulty });
  }

  private finishRun(): void {
    this.phase = 'finished';
    const timeMs = this.sim.finishT * 1000;
    const replay = this.recorder.build(this.track.seed, timeMs);
    const prevBest = this.pb?.timeMs ?? Infinity;
    const isBest = timeMs < prevBest;

    this.els.finishTime.textContent = fmt(timeMs);

    // Tracking: la run al traguardo è un punteggio (per un racer = il tempo).
    tracking.score(Number((timeMs / 1000).toFixed(2)), { track: this.track.id, best: isBest });
    if (isBest) tracking.achievement('personal-best', { track: this.track.id, timeMs: Math.round(timeMs) });

    if (isBest) {
      this.els.finishNote.textContent = t('finish.best');
      this.els.finishNote.classList.add('best');
      this.savePB(replay);
    } else {
      const delta = (timeMs - prevBest) / 1000;
      this.els.finishNote.textContent = `+${delta.toFixed(2)}s ${t('finish.ghost')}`;
      this.els.finishNote.classList.remove('best');
    }

    if (!this.tutorialDone) {
      this.tutorialDone = true;
      try {
        localStorage.setItem(TUTORIAL_KEY, '1');
      } catch {
        /* no storage */
      }
    }
    this.els.finish.classList.remove('hidden');
  }

  // --- Loop ---

  private frame(ts: number): void {
    if (this.lastFrame === 0) this.lastFrame = ts;
    let dt = (ts - this.lastFrame) / 1000;
    this.lastFrame = ts;
    if (dt > 0.25) dt = 0.25;

    if (this.screen === 'menu') {
      this.menuPan += dt * 90;
      this.renderer.drawBackdrop(this.menuPan);
      requestAnimationFrame((n) => this.frame(n));
      return;
    }

    if (this.phase === 'ready' && this.input.isDown()) this.startCountdown();

    let countdownTxt: string | null = null;
    if (this.phase === 'countdown') {
      const remain = COUNTDOWN_MS - (ts - this.countdownStart);
      if (remain <= 0) this.startRun();
      else {
        const n = Math.ceil(remain / 800);
        countdownTxt = n <= 1 ? t('go') : String(n - 1);
      }
    }

    if (this.phase === 'running') {
      this.acc += dt;
      let steps = 0;
      while (this.acc >= C.SIM_DT && steps < 8) {
        this.prev = cloneState(this.sim);
        const down = this.input.isDown();
        this.recorder.set(down, this.sim.t);
        step(this.sim, this.terrain, down);
        this.acc -= C.SIM_DT;
        steps++;
        if (this.sim.finished) {
          this.finishRun();
          break;
        }
      }
    }

    const alpha = this.phase === 'running' ? this.acc / C.SIM_DT : 1;
    const view = this.interpolated(alpha);
    const showGhost = this.phase === 'running' || this.phase === 'finished';
    const ghostPos = showGhost ? this.ghostAt(view.t) : this.ghostAt(0);

    // Boost (pompata efficace) + cue onboarding: dipendono dalla pendenza sotto il rider.
    let boost = 0;
    let cue: { text: string; down: boolean } | null = null;
    if (this.phase === 'running') {
      const slope = this.terrain.slopeAt(view.x);
      const held = this.input.isDown();
      if (view.grounded && held && slope.ty > 0.06) {
        boost = clamp((slope.ty - 0.06) * 2.4, 0, 1);
      }
      if (!this.tutorialDone && view.t < 12) {
        const downhill = slope.ty > 0.04;
        cue = { text: downhill ? t('cue.hold') : t('cue.release'), down: downhill };
      }
    }

    const shake = view.wipeTimer > 0 ? Math.min(1, view.wipeTimer / C.WIPE_TIME) : 0;
    this.renderer.draw(view, {
      ghost: ghostPos,
      running: this.phase === 'running',
      countdown: countdownTxt,
      shake,
      boost,
      cue,
    });

    this.els.timer.textContent = fmt(view.t * 1000);
    requestAnimationFrame((n) => this.frame(n));
  }

  private interpolated(alpha: number): SimState {
    const a = this.prev;
    const b = this.sim;
    return {
      ...b,
      x: lerp(a.x, b.x, alpha),
      y: lerp(a.y, b.y, alpha),
      vx: lerp(a.vx, b.vx, alpha),
      vy: lerp(a.vy, b.vy, alpha),
      t: lerp(a.t, b.t, alpha),
    };
  }

  private ghostAt(time: number): { x: number; y: number } | null {
    const g = this.ghost;
    if (!g || g.length === 0) return null;
    if (time <= g[0]!.t) return { x: g[0]!.x, y: g[0]!.y };
    const last = g[g.length - 1]!;
    if (time >= last.t) return { x: last.x, y: last.y };
    let lo = 0;
    let hi = g.length - 1;
    while (lo + 1 < hi) {
      const mid = (lo + hi) >> 1;
      if (g[mid]!.t <= time) lo = mid;
      else hi = mid;
    }
    const f0 = g[lo]!;
    const f1 = g[hi]!;
    const span = f1.t - f0.t || 1;
    const k = (time - f0.t) / span;
    return { x: lerp(f0.x, f1.x, k), y: lerp(f0.y, f1.y, k) };
  }
}
